import { ApolloCache } from '@apollo/client';
import { TableFilter as FilterTable, buildMatcher } from '@copera/common/board';
import { boardDoc } from '@modules/board/hooks/useBoardsQuery';
import {
  type Board,
  type BoardTableDataQuery,
  type BoardTableDataQueryVariables,
  type BoardsQuery,
  type BoardsQueryVariables,
  type Table,
  type TableColumns,
  type TableFilter,
  type TableRow,
  type TableRowActivitiesQuery,
  type TableRowActivitiesQueryVariables,
  type TableRowColumn,
  type TableRowComment,
  type TableRowsQuery,
  type TableRowsQueryVariables,
  type TableViewDataQueryVariables,
} from 'src/generated/graphql';
import { TableRowActivitiesDoc } from '../../../../modules/board/BoardTableRowDialog/ViewRowActivities/useRowActivitiesQuery';
import {
  type TableViewQueryData,
  tableViewRowsDoc,
} from '../BoardPage/TableViewPage/useTableViewQuery';
import { boardTableViewDataDoc } from './useBoardTableQuery';
import { TABLE_ROWS_QUERY } from './useTableRowsQuery';
import { getPositionForIndex } from './utils';

export const addTaskToBoardCache = (
  cache: ApolloCache<any>,
  {
    boardId,
    tableId,
    rows,
  }: {
    boardId: string;
    tableId: string;
    rows: TableRow[];
  },
  filter?: TableFilter,
  groupBy?: string,
) => {
  cache.updateQuery<TableRowsQuery, TableRowsQueryVariables>(
    {
      query: TABLE_ROWS_QUERY,
      variables: {
        input: {
          boardId,
          tableId,
        },
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { tableRows } = queryResult;

      const matcher = filter ? buildMatcher(filter as FilterTable) : null;

      let rowsMatched = rows;
      let unmatchedIds: string[] = [];

      if (matcher && filter?.conditions?.length) {
        [rowsMatched, unmatchedIds] = rows.reduce(
          (acc: any, curr) => {
            matcher(curr) ? acc[0].push(curr) : acc[1].push(curr._id);
            return acc;
          },
          [[], []],
        );
      }

      const rowsIds = tableRows.map((r) => r._id);
      const rowsToAdd = rowsMatched.filter((r) => !rowsIds.includes(r._id));

      const rowsUpdated = tableRows
        .filter((r) => !unmatchedIds.includes(r._id))
        .map((r) => {
          const newRow = rows.find((row) => row._id === r._id);
          return { ...r, ...(newRow || {}) };
        });

      return {
        tableRows: [...rowsUpdated, ...rowsToAdd],
      };
    },
  );
};

export const updateColsTasksFromCache = (
  cache: ApolloCache<any>,
  {
    boardId,
    tableId,
    rowIds,
    columns,
  }: {
    boardId: string;
    tableId: string;
    rowIds: string;
    columns: Record<string, TableRowColumn>;
  },
) => {
  cache.updateQuery<TableRowsQuery, TableRowsQueryVariables>(
    {
      query: TABLE_ROWS_QUERY,
      variables: {
        input: {
          boardId,
          tableId,
        },
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { tableRows } = queryResult;

      const newColsIds = Object.keys(columns);
      const newCols = Object.values(columns).map((c) => ({
        ...c,
        __typename: 'TableRowColumn',
      })) as any;

      return {
        tableRows: tableRows.map((row) => {
          if (rowIds?.includes(row._id)) {
            const oldCols = row.columns.filter(
              (c) => !newColsIds.includes(c.columnId),
            );
            return { ...row, columns: [...oldCols, ...newCols] };
          }
          return row;
        }),
      };
    },
  );
};

export const removeTableRowsFromCache = (
  cache: ApolloCache<any>,
  {
    boardId,
    tableId,
    rowIds,
  }: {
    boardId: string;
    tableId: string;
    rowIds: string[];
  },
) => {
  cache.updateQuery<TableRowsQuery, TableRowsQueryVariables>(
    {
      query: TABLE_ROWS_QUERY,
      variables: {
        input: {
          boardId,
          tableId,
        },
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { tableRows } = queryResult;

      return {
        tableRows: tableRows.filter((row) => !rowIds.includes(row._id)),
      };
    },
  );
};

export const addBoardToCache = (
  cache: ApolloCache<any>,
  {
    board,
  }: {
    board: Board;
  },
) => {
  cache.updateQuery<BoardsQuery, BoardsQueryVariables>(
    {
      query: boardDoc,
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { boards } = queryResult;

      if (boards.find((b) => b._id === board._id)) return;

      return {
        boards: [...boards, board],
      };
    },
  );
};

export const updateBoardRolesToCache = (
  cache: ApolloCache<any>,
  {
    updatedBoard,
  }: {
    updatedBoard: Board;
  },
) => {
  cache.modify({
    id: cache.identify({
      __typename: 'Board',
      id: updatedBoard._id,
    }),
    fields: {
      roles() {
        return { ...updatedBoard.roles };
      },
    },
  });
};

export const addBoardTableToCache = (
  cache: ApolloCache<any>,
  {
    table,
  }: {
    table: Table;
  },
) => {
  cache.updateQuery<BoardsQuery, BoardsQueryVariables>(
    {
      query: boardDoc,
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { boards } = queryResult;
      const tables = boards.flatMap((b) => b.tables);

      if (tables?.find((t) => t._id === table._id)) return { boards };

      return {
        boards: boards.map((b) =>
          b._id === table.board
            ? {
                ...b,
                tables: [
                  ...b.tables,
                  { ...table, __typename: 'Table', views: [] },
                ],
              }
            : b,
        ),
      };
    },
  );
};

export const addTableRowCommentToCache = (
  cache: ApolloCache<any>,
  {
    comment,
  }: {
    comment: TableRowComment;
  },
) => {
  cache.updateQuery<TableRowActivitiesQuery, TableRowActivitiesQueryVariables>(
    {
      query: TableRowActivitiesDoc,
      variables: {
        input: {
          boardId: comment.boardId,
          tableId: comment.tableId,
          rowId: comment.rowId,
        },
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { tableRowComments } = queryResult;

      if (tableRowComments.items.find((c) => c._id === comment._id)) return;

      return {
        tableRowComments: {
          ...tableRowComments,
          items: [...tableRowComments.items, comment],
        },
      };
    },
  );
};

export const addTableColumnToCache = (
  cache: ApolloCache<any>,
  {
    tableColumn,
    boardId,
  }: {
    tableColumn: TableColumns;
    boardId: string;
  },
) => {
  cache.updateQuery<BoardTableDataQuery, BoardTableDataQueryVariables>(
    {
      query: boardTableViewDataDoc,
      variables: {
        boardId,
        tableId: tableColumn.tableId,
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { table } = queryResult;

      if (table.columns.find((c) => c._id === tableColumn._id)) return;

      addTableViewColumnToCache(cache, {
        tableColumn,
        boardId,
        viewId: table.views[0]._id,
      });

      return {
        table: {
          ...table,
          columns: [...table.columns, tableColumn],
          views: {
            ...table.views,
          },
        },
      };
    },
  );
};

export const addTableViewColumnToCache = (
  cache: ApolloCache<any>,
  {
    tableColumn,
    boardId,
    viewId,
  }: {
    tableColumn: TableColumns;
    boardId: string;
    viewId: string;
  },
) => {
  cache.updateQuery<
    { tableView: TableViewQueryData },
    TableViewDataQueryVariables
  >(
    {
      query: tableViewRowsDoc,
      variables: {
        input: {
          viewId,
          boardId,
          tableId: tableColumn.tableId,
        },
      },
    },
    (queryResult) => {
      if (!queryResult) return queryResult;
      const { columns } = queryResult.tableView;

      if (columns?.find((c) => c.columnId === tableColumn._id)) return;

      const order = getPositionForIndex(
        columns.map((c) => c.order),
        columns.length,
      );

      return {
        tableView: {
          ...queryResult.tableView,
          columns: [
            ...columns,
            {
              order,
              width: null,
              fixed: false,
              hidden: false,
              columnId: tableColumn._id,
              _id: `${tableColumn.columnRef}`,
              __typename: 'TableViewColumn',
            },
          ],
        },
      };
    },
  );
};
